import { Box, Grid, Pagination, styled, Typography } from '@mui/material';
import { BankAccountOperationStatus } from 'further-types/lp/bank-reconciliation';
import api from 'lib/trpcClient';
import { ceil } from 'lodash';
import React, { useState } from 'react';
import FindAndMatch from './FindAndMatch';
import ErrorBoundary from 'components/ErrorBoundary';
import BankOperation from './BankOperation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';

const BankOperationsHeading = styled(Grid)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const PaginationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  borderTop: `1px solid ${theme.palette.borderColor?.dark}`,
  paddingTop: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    borderTop: 'none',
  },
}));

type Props = {
  bankAccount: {
    id: string;
    vehicleId: string;
    accountCurrency: CurrencyCode;
  };
};

const ReconcileTransactions: React.FC<Props> = ({ bankAccount }) => {
  const [page, setPage] = useState(1);
  const [selectedBankOperationId, setSelectedBankOperationId] =
    useState<string>();
  const [preselectedTransactionId, setPreselectedTransactionId] =
    useState<string>();

  const [data] = api.bankAccount.listBankAccountOperations.useSuspenseQuery({
    bankAccountId: bankAccount.id,
    vehicleId: bankAccount.vehicleId,
    status: BankAccountOperationStatus.Unreconciled,
    page,
    rowsPerPage: 10,
    order: 'asc',
    orderBy: 'date',
  });

  const [reconcilableTransactions] =
    api.reconciliation.listTransactions.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
    });

  const handleShowFindAndMatch = (
    bankOperationId: string,
    transactionId?: string,
  ) => {
    setSelectedBankOperationId(bankOperationId);
    setPreselectedTransactionId(transactionId);
  };

  return (
    <Column spacing="md">
      <Grid container spacing={6}>
        <BankOperationsHeading size={{ xs: 12, md: 5 }}>
          <Typography variant="h5">Review bank statement lines</Typography>
        </BankOperationsHeading>
        <Grid size={{ xs: 0, md: 2 }}></Grid>
        <BankOperationsHeading size={{ xs: 12, md: 5 }}>
          <Typography variant="h5">
            Match with transactions on Further
          </Typography>
        </BankOperationsHeading>
      </Grid>
      {data.results.map((bankOperation) => (
        <React.Fragment key={bankOperation.id}>
          <BankOperation
            bankAccount={bankAccount}
            {...bankOperation}
            reconcilableTransactions={reconcilableTransactions}
            onFindAndMatch={handleShowFindAndMatch}
          />
          {selectedBankOperationId === bankOperation.id && (
            <ErrorBoundary>
              <FindAndMatch
                vehicleId={bankAccount.vehicleId}
                bankAccountId={bankAccount.id}
                preselectedTransactionId={preselectedTransactionId}
                bankOperation={bankOperation}
                defaultReconcilableTransactions={reconcilableTransactions}
                onClose={() => setSelectedBankOperationId(undefined)}
              />
            </ErrorBoundary>
          )}
        </React.Fragment>
      ))}
      <PaginationRow>
        <Pagination
          color="primary"
          count={ceil(data.totalCount / 10)}
          showFirstButton
          showLastButton
          page={page}
          onChange={(_, page) => setPage(page)}
        />
      </PaginationRow>
    </Column>
  );
};

export default ReconcileTransactions;
